import { useMemo } from "react";
import { useGetIdentity, useGetList, useNotify } from "ra-core";
import { Flag } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { FEEDBACK_CATEGORIES } from "../feedback/feedbackTaxonomy";
import type { EchoflicksVideoSession } from "../types";
import {
  SCENE_FLAG_TAG_LABELS,
  groupSceneFlags,
  i2vAttemptsAnchorId,
  residualMotionLabel,
  type EchoflicksSessionSceneFlag,
  type SceneFlagGroup,
  type SceneFlagSnapshot,
} from "./sceneFlags";
import { formatDateTime } from "./sessionUtils";

const CATEGORY_LABELS = new Map<string, string>(
  FEEDBACK_CATEGORIES.map((category) => [category.id, category.label]),
);

const short = (value: string | null, length = 8) =>
  value ? value.slice(0, length) : null;

const SnapshotFacts = ({ snapshot }: { snapshot: SceneFlagSnapshot }) => {
  const facts = [
    [snapshot.provider, snapshot.concreteProvider].filter(Boolean).join(" / "),
    snapshot.model,
    snapshot.clipSha256 ? `clip ${short(snapshot.clipSha256)}` : null,
    snapshot.configReleaseDigest
      ? `config ${short(snapshot.configReleaseDigest)}`
      : null,
    snapshot.inherited ? "clip inherited from the parent film" : null,
  ].filter((fact): fact is string => Boolean(fact));
  const budget = snapshot.reMotion;
  const held = Array.isArray(budget?.held)
    ? (budget.held as unknown[]).filter(
        (item): item is string => typeof item === "string",
      )
    : [];
  return (
    <div className="space-y-1 text-xs text-muted-foreground">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {facts.length ? <span>{facts.join(" · ")}</span> : null}
        <Badge variant="outline" className="font-normal">
          snapshot {snapshot.status ?? "unknown"}
          {snapshot.missing.length
            ? ` · ${snapshot.missing.length} missing`
            : ""}
        </Badge>
      </div>
      {budget ? (
        <div>
          Motion budget: {String(budget.setting ?? "unknown setting")}
          {held.length ? ` · held: ${held.join(", ")}` : ""}
          {budget.camera_only === true ? " · camera only" : ""}
        </div>
      ) : null}
      {snapshot.promptExcerpt ? (
        <p className="line-clamp-3 font-mono" title={snapshot.promptExcerpt}>
          {snapshot.promptExcerpt}
          {snapshot.promptChars != null &&
          snapshot.promptChars > snapshot.promptExcerpt.length
            ? ` … (${snapshot.promptChars} chars)`
            : ""}
        </p>
      ) : null}
    </div>
  );
};

const FlagRow = ({ flag }: { flag: EchoflicksSessionSceneFlag }) => (
  <li
    className={`space-y-1 text-sm ${flag.status === "withdrawn" ? "opacity-60" : ""}`}
  >
    <div className="flex flex-wrap items-center gap-2">
      {flag.tags.map((tag) => (
        <Badge key={tag} variant="secondary">
          {SCENE_FLAG_TAG_LABELS[tag]}
        </Badge>
      ))}
      {flag.status === "withdrawn" ? (
        <Badge variant="outline">withdrawn</Badge>
      ) : null}
      {flag.feedback_categories.length ? (
        <span className="text-xs text-muted-foreground">
          Feedback taxonomy:{" "}
          {flag.feedback_categories
            .map((id) => CATEGORY_LABELS.get(id) ?? id)
            .join(" · ")}
        </span>
      ) : null}
    </div>
    {flag.note ? <p className="whitespace-pre-wrap">{flag.note}</p> : null}
    <div className="text-xs text-muted-foreground">
      {flag.reporter_email ?? "staff"} · {formatDateTime(flag.created_at)}
      {flag.withdrawn_at
        ? ` · withdrawn ${formatDateTime(flag.withdrawn_at)}`
        : ""}
    </div>
  </li>
);

const SceneGroup = ({
  group,
  onShowAttempts,
}: {
  group: SceneFlagGroup;
  onShowAttempts: (sceneId: number) => void;
}) => {
  const { latest } = group;
  const verdict = residualMotionLabel(
    latest.residual_motion,
    latest.qc_report_status,
  );
  return (
    <section
      className="space-y-2 rounded-md border p-3"
      data-testid={`scene-flags-scene-${group.sceneId ?? "unknown"}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">
            Scene {group.sceneIndex != null ? group.sceneIndex + 1 : "?"}
          </span>
          <span className="text-xs text-muted-foreground">
            id {group.sceneId ?? "unknown"}
          </span>
          <Badge variant={verdict.variant} className="font-normal">
            {verdict.text}
          </Badge>
          {latest.residual_motion?.attempt != null ? (
            <span className="text-xs text-muted-foreground">
              attempt {latest.residual_motion.attempt}
              {latest.residual_motion.matchedFlaggedClip
                ? ""
                : " (not the flagged clip)"}
            </span>
          ) : null}
        </div>
        {group.sceneId != null ? (
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0"
            onClick={() => onShowAttempts(group.sceneId as number)}
          >
            i2v attempts
          </Button>
        ) : null}
      </div>
      <SnapshotFacts snapshot={latest.snapshot} />
      {latest.qc_findings.length ? (
        <ul className="space-y-1 text-xs">
          {latest.qc_findings.map((finding, index) => (
            <li key={`${finding.code}-${index}`}>
              <Badge variant="outline" className="mr-2 font-normal">
                {finding.severity ?? "finding"}
              </Badge>
              {finding.message ?? finding.code}
            </li>
          ))}
        </ul>
      ) : null}
      <ul className="space-y-2 border-t pt-2">
        {group.flags.map((flag) => (
          <FlagRow key={flag.id} flag={flag} />
        ))}
      </ul>
    </section>
  );
};

/**
 * C2: the scenes Echoflicks staff flagged on this Studio film from the portal,
 * each with the prompt/clip snapshot the bot froze at flag time and the
 * residual-motion QC verdict of that clip. Administrator-only (the dashboard API
 * and the CRM proxy enforce the same claim); renders nothing when the film has
 * no flags, which is every WhatsApp film.
 */
export const SceneFlagsCard = ({
  session,
}: {
  session: EchoflicksVideoSession;
}) => {
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const isAdmin = Boolean(identity?.administrator);
  const { data, error } = useGetList<EchoflicksSessionSceneFlag>(
    "echoflicks_session_scene_flags",
    {
      filter: { session_id: session.id },
      pagination: { page: 1, perPage: 500 },
    },
    { enabled: isAdmin && session.id != null },
  );
  const groups = useMemo(() => groupSceneFlags(data ?? []), [data]);

  if (!isAdmin) return null;
  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Staff scene flags</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          The scene flags could not be loaded.
        </CardContent>
      </Card>
    );
  }
  if (!groups.length) return null;

  const active = groups.reduce((total, group) => total + group.activeCount, 0);
  const showAttempts = (sceneId: number) => {
    const target = document.getElementById(i2vAttemptsAnchorId(sceneId));
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      notify("No i2v attempts are shown for this scene.", { type: "info" });
    }
  };

  return (
    <Card data-testid="scene-flags-card">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            <Flag className="size-4" />
            Staff scene flags
          </CardTitle>
          <Badge variant={active ? "destructive" : "outline"}>
            {active} active
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          Flagged by Echoflicks staff from the studio. Each scene shows the clip
          and prompt as they were when flagged, and the residual-motion check of
          that clip.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {groups.map((group) => (
          <SceneGroup
            key={String(group.sceneId ?? "unknown")}
            group={group}
            onShowAttempts={showAttempts}
          />
        ))}
      </CardContent>
    </Card>
  );
};
